import React, { useState, useEffect, useMemo } from "react";
import Sidebar from "../../../Sidebar/Sidebar";
import Header from "../../../Header/Header";
import { FaTrash, FaEdit, FaBell, FaChevronLeft, FaChevronRight, FaTimes, FaExclamationTriangle } from "react-icons/fa";
import { HiOutlineSearch, HiOutlineDocumentText, HiOutlineCalendar, HiOutlineClock } from "react-icons/hi";
import { MdRemoveRedEye, MdEventNote } from "react-icons/md";
import { motion, AnimatePresence } from "framer-motion";
import AddNotice from "./Components/AddNotice";
import Skeleton from "react-loading-skeleton";
import UpdateNoticeModal from "./Components/UpdateNotice";
import "react-loading-skeleton/dist/skeleton.css";
import Cookies from "js-cookie";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const Notice = () => {
  // ✅ Sidebar state and toggle
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

  // ✅ ALL ORIGINAL STATE
  const [notices, setNotices] = useState([]);
  const [filteredNotices, setFilteredNotices] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedNotice, setSelectedNotice] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [noticeToDelete, setNoticeToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState('newest'); // 'newest', 'oldest'
  const itemsPerPage = 10;
  const authToken = Cookies.get("authToken");

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Calculate notice statistics
  const stats = useMemo(() => {
    const total = notices.length;
    const today = new Date();
    const active = notices.filter(n => new Date(n.startDate) >= today).length;
    const expired = notices.filter(n => new Date(n.startDate) < today).length;
    return { total, active, expired };
  }, [notices]);

  // Filter and sort notices
  useEffect(() => {
    let filtered = [...notices];
    
    // Apply search filter
    if (debouncedSearchTerm.trim() !== "") {
      filtered = filtered.filter(notice => 
        notice.title?.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
        notice.description?.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
      );
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      const dateA = new Date(a.startDate);
      const dateB = new Date(b.startDate);
      return sortBy === 'newest' ? dateB - dateA : dateA - dateB;
    });
    
    setFilteredNotices(filtered);
    setCurrentPage(1);
  }, [debouncedSearchTerm, notices, sortBy]);

  useEffect(() => {
    const fetchNotices = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/apis/admin/getAllNotice`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (!data.success || !Array.isArray(data.data)) {
          throw new Error("Invalid data format received from server");
        }

        setNotices(data.data);
        setFilteredNotices(data.data);
        setError(null);
        
        
      } catch (err) {
        console.error("Error fetching notices:", err);
        setError("Failed to load notices: " + err.message);
        setNotices([]);
        setFilteredNotices([]);
        toast.error("Failed to load notices");
      } finally {
        setLoading(false);
      }
    };

    fetchNotices();
  }, [authToken]);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const currentData = filteredNotices.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const totalPages = Math.ceil(filteredNotices.length / itemsPerPage);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    if (date.toDateString() === today.toDateString()) {
      return `Today, ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    } else if (date.toDateString() === tomorrow.toDateString()) {
      return `Tomorrow, ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    } else {
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric'
      });
    }
  };

  const isExpired = (dateString) => {
    return new Date(dateString) < new Date();
  };

  const getStatusBadge = (dateString) => {
    const expired = isExpired(dateString);
    return expired ? (
      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-200 text-gray-700 border border-gray-300">
        <HiOutlineClock className="w-3 h-3 mr-1" />
        Expired
      </span>
    ) : (
      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
        <HiOutlineCalendar className="w-3 h-3 mr-1" />
        Active
      </span>
    );
  };

  const openUpdateModal = (notice) => {
    setSelectedNotice(notice);
    setShowUpdateModal(true);
  };

  const closeUpdateModal = () => {
    setShowUpdateModal(false);
    setSelectedNotice(null);
  };

  const handleNoticeUpdate = (updatedNotice) => {
    setNotices((prev) =>
      prev.map((n) => (n._id === updatedNotice._id ? updatedNotice : n))
    );
    toast.success("Notice updated successfully");
  };

  const openModal = (notice) => {
    setSelectedNotice(notice);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedNotice(null);
  };

  const openAddModal = () => {
    setShowAddModal(true);
  };

  const closeAddModal = () => {
    setShowAddModal(false);
  };

  const handleAddNotice = (newNotice) => {
    setNotices([newNotice, ...notices]);
    toast.success("Notice added successfully");
  };

  const openDeleteModal = (notice) => {
    setNoticeToDelete(notice);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setNoticeToDelete(null);
    setIsDeleting(false);
  };

  const handleDelete = async () => {
    if (!noticeToDelete) return;
    
    try {
      setIsDeleting(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/apis/admin/deleteNotice/${noticeToDelete._id}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );
  
      const data = await response.json();
  
      if (response.ok && data.success) {
        setNotices((prev) => prev.filter((n) => n._id !== noticeToDelete._id));
        toast.success("Notice deleted successfully");
        closeDeleteModal();
      } else {
        throw new Error(data.message || "Failed to delete the notice");
      }
    } catch (error) {
      console.error("Error deleting notice:", error);
      toast.error(error.message || "Failed to delete notice");
    } finally {
      setIsDeleting(false);
    }
  };

  const StatCard = ({ title, value, icon: Icon, color }) => (
    <motion.div
      whileHover={{ y: -2 }}
      className="bg-white rounded-lg p-4 shadow-sm border border-gray-200"
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <p className="text-2xl font-bold text-gray-800">{value}</p>
        </div>
        <div className={`p-3 rounded-lg bg-${color}-100`}>
          <Icon className={`w-5 h-5 text-${color}-600`} />
        </div>
      </div>
    </motion.div>
  );

  const renderSkeletonCards = () => {
    return Array(3).fill(0).map((_, i) => (
      <div key={i} className="bg-white border border-gray-200 rounded-xl p-4">
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <Skeleton width="60%" height={20} />
            <Skeleton width="40%" height={16} className="mt-2" />
          </div>
          <Skeleton width={60} height={24} />
        </div>
        <Skeleton width="100%" height={40} className="mt-3" />
        <div className="flex justify-between items-center mt-3 pt-3 border-t border-gray-200">
          <Skeleton width={80} height={16} />
          <div className="flex gap-2">
            <Skeleton width={32} height={32} circle />
            <Skeleton width={32} height={32} circle />
            <Skeleton width={32} height={32} circle />
          </div>
        </div>
      </div>
    ));
  };

  const renderSkeletonRows = () => {
    return Array(5).fill(0).map((_, index) => (
      <tr key={`skeleton-${index}`} className="even:bg-gray-50">
        <td className="px-6 py-4"><Skeleton width={30} /></td>
        <td className="px-6 py-4"><Skeleton width={150} /></td>
        <td className="px-6 py-4"><Skeleton width={200} /></td>
        <td className="px-6 py-4"><Skeleton width={100} /></td>
        <td className="px-6 py-4">
          <div className="flex gap-2">
            <Skeleton width={32} height={32} circle />
            <Skeleton width={32} height={32} circle />
          </div>
        </td>
      </tr>
    ));
  };

  const renderEmptyState = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="text-center py-16 px-4"
    >
      <div className="inline-flex items-center justify-center w-20 h-20 bg-gray-100 rounded-full mb-4">
        <HiOutlineDocumentText className="w-10 h-10 text-gray-400" />
      </div>
      <h3 className="text-lg font-medium text-gray-900 mb-2">
        {searchTerm ? 'No notices found' : 'No notices yet'}
      </h3>
      <p className="text-gray-500 mb-6">
        {searchTerm 
          ? `No notices matching "${searchTerm}"`
          : 'Create your first notice to keep everyone informed'}
      </p>
      {searchTerm && (
        <button
          onClick={() => setSearchTerm('')}
          className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
        >
          <FaTimes className="w-4 h-4 mr-2" />
          Clear search
        </button>
      )}
    </motion.div>
  );

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 w-full">
      <ToastContainer position="top-right" autoClose={3000} />
      
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} activePage="/notice" />
      
      <div className="flex-1 w-0">
        <Header toggleSidebar={toggleSidebar} />
        
        <main className="p-4 lg:p-6 overflow-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-lg p-4 lg:p-6 border border-gray-200"
          >
            {/* Header with stats */}
            <div className="mb-6">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
                <div>
                  <h1 className="text-2xl lg:text-3xl font-bold text-gray-800 flex items-center gap-2">
                    <FaBell className="w-8 h-8 text-gray-600" />
                    Notice Management
                  </h1>
                  <p className="text-sm text-gray-500 mt-1">
                    {filteredNotices.length} notice{filteredNotices.length !== 1 ? 's' : ''} found
                    {searchTerm && ` for "${searchTerm}"`}
                  </p>
                </div>
                
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto">
                  <div className="relative flex-1 sm:min-w-[300px]">
                    <HiOutlineSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="text"
                      placeholder="Search notices..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent bg-white text-gray-700 transition-shadow"
                      aria-label="Search notices"
                    />
                    {searchTerm && (
                      <button
                        onClick={() => setSearchTerm('')}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        aria-label="Clear search"
                      >
                        <FaTimes className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  
                  <button
                    onClick={openAddModal}
                    className="w-full sm:w-auto bg-gray-800 hover:bg-gray-900 text-white px-4 py-2.5 rounded-lg flex items-center justify-center gap-2 text-sm transition-colors shadow-sm"
                  >
                    <span>+ Add Notice</span>
                  </button>
                </div>
              </div>

              {/* Stats Cards */}
              {!loading && notices.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
                  <StatCard title="Total Notices" value={stats.total} icon={HiOutlineDocumentText} color="gray" />
                  <StatCard title="Active" value={stats.active} icon={HiOutlineCalendar} color="green" />
                  <StatCard title="Expired" value={stats.expired} icon={HiOutlineClock} color="gray" />
                </div>
              )}
            </div>

            {/* Sort Options */}
            <div className="flex justify-end mb-4">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-400 bg-white text-gray-700"
              >
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
              </select>
            </div>

            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg mb-4 text-sm"
              >
                {error}
              </motion.div>
            )}

            {/* Mobile Card View */}
            <div className="block lg:hidden">
              <AnimatePresence mode="wait">
                {loading ? (
                  <motion.div
                    key="loading"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="space-y-3"
                  >
                    {renderSkeletonCards()}
                  </motion.div>
                ) : currentData.length === 0 ? (
                  renderEmptyState()
                ) : (
                  <motion.div
                    key="content"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="space-y-3"
                  >
                    {currentData.map((notice, index) => (
                      <motion.div
                        key={notice._id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition-all"
                      >
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xs text-gray-400">#{(currentPage - 1) * itemsPerPage + index + 1}</span>
                              <h3 className="font-semibold text-gray-800">{notice.title}</h3>
                            </div>
                            <div className="flex items-center gap-2 mt-1">
                              {getStatusBadge(notice.startDate)}
                              <span className="text-xs text-gray-400">
                                {formatDate(notice.startDate)}
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        <p className="text-sm text-gray-600 line-clamp-2 mb-3">
                          {notice.description}
                        </p>
                        
                        <div className="flex items-center justify-end gap-2 pt-3 border-t border-gray-100">
                          <button
                            onClick={() => openModal(notice)}
                            className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                            title="View details"
                          >
                            <MdRemoveRedEye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => openUpdateModal(notice)}
                            className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                            title="Edit notice"
                          >
                            <FaEdit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => openDeleteModal(notice)}
                            className="p-2 text-gray-500 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete notice"
                          >
                            <FaTrash className="w-4 h-4" />
                          </button>
                        </div>
                      </motion.div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Desktop Table View */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">#</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <AnimatePresence mode="wait">
                    {loading ? (
                      renderSkeletonRows()
                    ) : currentData.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="px-6 py-12">
                          {renderEmptyState()}
                        </td>
                      </tr>
                    ) : (
                      currentData.map((notice, index) => (
                        <motion.tr
                          key={notice._id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.03 }}
                          className="hover:bg-gray-50 transition-colors"
                        >
                          <td className="px-6 py-4 text-sm text-gray-600">
                            {(currentPage - 1) * itemsPerPage + index + 1}
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm font-medium text-gray-800">
                              {notice.title}
                            </span>
                          </td>
                          <td className="px-6 py-4 max-w-xs">
                            <p className="text-sm text-gray-600 line-clamp-2">
                              {notice.description}
                            </p>
                            <button
                              onClick={() => openModal(notice)}
                              className="text-xs text-gray-500 hover:text-gray-700 mt-1 font-medium"
                            >
                              View full description →
                            </button>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center text-sm text-gray-500">
                              <HiOutlineCalendar className="w-4 h-4 mr-2 text-gray-400" />
                              {formatDate(notice.startDate)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {getStatusBadge(notice.startDate)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => openUpdateModal(notice)}
                                className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                                title="Edit notice"
                              >
                                <FaEdit className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => openDeleteModal(notice)}
                                className="p-2 text-gray-500 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                title="Delete notice"
                              >
                                <FaTrash className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </motion.tr>
                      ))
                    )}
                  </AnimatePresence>
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {filteredNotices.length > itemsPerPage && (
              <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="text-sm text-gray-500">
                  Showing {(currentPage - 1) * itemsPerPage + 1} to{' '}
                  {Math.min(currentPage * itemsPerPage, filteredNotices.length)} of{' '}
                  {filteredNotices.length} notices
                </div>
                
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="p-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    aria-label="Previous page"
                  >
                    <FaChevronLeft className="w-4 h-4" />
                  </button>
                  
                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      
                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${
                            currentPage === pageNum
                              ? 'bg-gray-700 text-white'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                  
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="p-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    aria-label="Next page"
                  >
                    <FaChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        </main>
      </div>

      {/* Notice Details Modal */}
      <AnimatePresence>
        {isModalOpen && selectedNotice && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex justify-center items-center p-4 z-50"
            onClick={closeModal}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", duration: 0.3 }}
              className="bg-white rounded-xl shadow-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-gray-200"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-xl lg:text-2xl font-bold text-gray-800">
                  {selectedNotice.title}
                </h3>
                <button
                  onClick={closeModal}
                  className="text-gray-500 hover:text-gray-700 p-1 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <FaTimes className="w-5 h-5" />
                </button>
              </div>
              
              <div className="flex items-center gap-3 mb-4">
                {getStatusBadge(selectedNotice.startDate)}
                <span className="text-sm text-gray-500 flex items-center gap-1">
                  <HiOutlineCalendar className="w-4 h-4" />
                  {formatDate(selectedNotice.startDate)}
                </span>
              </div>
              
              <div className="prose max-w-none mb-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Description:</h4>
                <p className="text-sm lg:text-base text-gray-700 whitespace-pre-line leading-relaxed">
                  {selectedNotice.description}
                </p>
              </div>
              
              <div className="flex justify-end mt-6">
                <button
                  className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg text-sm transition-colors"
                  onClick={closeModal}
                >
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Update Notice Modal */}
      <AnimatePresence>
        {showUpdateModal && selectedNotice && (
          <UpdateNoticeModal
            notice={selectedNotice}
            onClose={closeUpdateModal}
            onUpdate={handleNoticeUpdate}
          />
        )}
      </AnimatePresence>

      {/* Add Notice Modal */}
      <AnimatePresence>
        {showAddModal && (
          <AddNotice
            onAddNotice={handleAddNotice}
            onClose={closeAddModal}
            authToken={authToken}
          />
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {showDeleteModal && noticeToDelete && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex justify-center items-center p-4 z-50"
            onClick={closeDeleteModal}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", duration: 0.3 }}
              className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md border border-gray-200"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-lg lg:text-xl font-bold text-gray-800">Delete Notice</h3>
                <button
                  onClick={closeDeleteModal}
                  className="text-gray-500 hover:text-gray-700 p-1 rounded-lg hover:bg-gray-100 transition-colors"
                  disabled={isDeleting}
                >
                  <FaTimes className="w-5 h-5" />
                </button>
              </div>
              
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-3 text-amber-600 bg-amber-50 p-3 rounded-lg">
                  <FaExclamationTriangle className="w-5 h-5 flex-shrink-0" />
                  <p className="text-sm">This action cannot be undone.</p>
                </div>
                <p className="text-sm lg:text-base text-gray-700">
                  Are you sure you want to delete the notice{' '}
                  <span className="font-semibold text-gray-800">"{noticeToDelete.title}"</span>?
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row justify-end gap-3">
                <button
                  onClick={closeDeleteModal}
                  className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm transition-colors"
                  disabled={isDeleting}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  className="w-full sm:w-auto px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center justify-center text-sm transition-colors disabled:opacity-50"
                  disabled={isDeleting}
                >
                  {isDeleting ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Deleting...
                    </>
                  ) : (
                    "Delete Notice"
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Notice;